import { type CamelCasedEducationExperience, type CamelCasedTranscript } from './CareerProfiles.types';

// Iguana-free ports of the transcript getters on the Angular EducationExperience and CareerProfile
// models (Careers/angularModule/scripts/models/). Each names the getter it replaces.

type EducationExperience = CamelCasedEducationExperience;

// EducationExperience#unofficialTranscripts
export function unofficialTranscripts(experience: EducationExperience): CamelCasedTranscript[] {
    return experience.transcripts.filter(transcript => transcript.transcriptType === 'unofficial');
}

// EducationExperience#transcriptWaived
function transcriptWaived(experience: EducationExperience): boolean {
    return !!experience.transcriptWaiver;
}

// EducationExperience#transcriptApprovedOrWaived
function transcriptApprovedOrWaived(experience: EducationExperience): boolean {
    return experience.transcriptApproved || transcriptWaived(experience);
}

// EducationExperience#canRequireOfficial. See also transcript_requirement_helper.rb.
function canRequireOfficial(experience: EducationExperience): boolean {
    return (
        !!experience.degreeProgram &&
        !experience.willNotComplete &&
        experience.graduationYear <= new Date().getFullYear()
    );
}

// EducationExperience#orgName
export function orgName(experience: EducationExperience): string {
    return experience.educationalOrganization?.text ?? '';
}

// CareerProfile#educationExperienceRequiringOfficialTranscript.
//
// The `transcriptStatus === 'official_approved'` branch is retained for fidelity with the Angular
// getter, but `transcript_status` is an ADMIN_FIELD stripped from the learner payload, so on the
// converted currentUser only the `officialTranscriptRequired` branch can match.
export function educationExperienceRequiringOfficialTranscript(
    educationExperiences: EducationExperience[],
): EducationExperience | null {
    return (
        educationExperiences.find(experience => experience.transcriptStatus === 'official_approved') ??
        educationExperiences.find(experience => experience.officialTranscriptRequired) ??
        null
    );
}

// CareerProfile#educationExperiencesThatCanRequireOfficial
export function educationExperiencesThatCanRequireOfficial(
    educationExperiences: EducationExperience[],
): EducationExperience[] {
    return educationExperiences.filter(canRequireOfficial);
}

// CareerProfile#indicatesUserShouldProvideTranscripts. See also career_profile.rb.
export function indicatesUserShouldProvideTranscripts(educationExperiences: EducationExperience[]): boolean {
    return !!educationExperienceRequiringOfficialTranscript(educationExperiences);
}

// CareerProfile#officialTranscriptApproved
export function officialTranscriptApproved(educationExperiences: EducationExperience[]): boolean {
    return !!educationExperienceRequiringOfficialTranscript(educationExperiences)?.transcriptApproved;
}

// CareerProfile#officialTranscriptApprovedOrWaived
export function officialTranscriptApprovedOrWaived(educationExperiences: EducationExperience[]): boolean {
    const experience = educationExperienceRequiringOfficialTranscript(educationExperiences);
    return !!experience && transcriptApprovedOrWaived(experience);
}
